import {
  Box,
  DebtRepaymentPlan,
  Disclaimer,
  Div,
  H5,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { formatDay } from '../helpers/receivablesLabels';
import {
  PLAN_STATUS_LABELS,
  planEndedAt,
} from '../helpers/repaymentPlanLabels';
import { PlanMethodChips } from './PlanMethodChips';
import { StatusBadge } from './ReceivablesCells';

type Props = {
  /** Newest first. */
  plans: DebtRepaymentPlan[];
};

/**
 * The plans a location had before: what each collected, from when to when,
 * and the note it was started or stopped with. Renders an active one too, if
 * it is handed one.
 */
export const RepaymentPlanHistory = ({ plans }: Props) => {
  // Read at render time: theme values from the barrel are undefined at the
  // module top level of an eagerly loaded file.
  const statusColor = {
    active: lightTheme.green,
    completed: lightTheme.blue,
    cancelled: lightTheme.lightGray,
  };

  return (
    <Div $flex $column $gap="8px">
      {plans.map((plan) => {
        const endedAt = planEndedAt(plan);
        return (
          <Box
            key={plan.id}
            $flex
            $alignCenter
            $between
            $gap="16px"
            $gapSM="4px"
            $columnSM
            $alignStartSM
          >
            <Div $minWidth="0">
              <Div $flex $alignCenter $gap="8px" $mb="8">
                <H5 $m="0">Plan #{plan.id}</H5>
                <StatusBadge color={statusColor[plan.status]}>
                  {PLAN_STATUS_LABELS[plan.status]}
                </StatusBadge>
              </Div>
              <Div $mb="8">
                <PlanMethodChips plan={plan} />
              </Div>
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                {formatDay(plan.createdAt)}
                {endedAt ? ` - ${formatDay(endedAt)}` : ' - now'}
              </Disclaimer>
              {/* 4px under the dates, as on the active plan's card. */}
              {plan.note && (
                <Disclaimer $m="4px 0 0">Note: {plan.note}</Disclaimer>
              )}
            </Div>
            {/* One "Repaid: $x" line on a phone, under the dates. */}
            <Div
              $flex
              $column
              $alignEnd
              $rowSM
              $alignCenterSM
              $gapSM="6px"
              $flexNone
            >
              <Disclaimer $m="0">Repaid</Disclaimer>
              <H5 $m="0" $color={lightTheme.green}>
                {displayCentsInDollars(plan.repaidAmount)}
              </H5>
            </Div>
          </Box>
        );
      })}
    </Div>
  );
};
